import {Component, computed, inject, input, output, signal} from '@angular/core';
import {FermetureClientService} from '../../../core/services/entities/fermeture-client.service';
import {JourSemaine} from '../../../core/enums/client/jour-semaine.enum';
import {FermetureTabEnum} from '../fermeture-tab.enum';
import {HierarchyPropertyDTO} from '../../../core/dtos/client/fermeture/fermeture-client-hierarchy-dto';
import {FermetureJourSemaineDTO} from '../../../core/dtos/client/fermeture/fermeture-jour-semaine-dto';
import {FermeturePeriodeDTO} from '../../../core/dtos/client/fermeture/fermeture-periode-dto';
import {ButtonThemeEnum} from '../../../shared/ui/button/button-theme-enum';

@Component({
  selector: 'yo-fermeture-add-area',
  templateUrl: './fermeture-add-area.component.html',
  styleUrls: ['./fermeture-add-area.component.scss'],
  standalone: false
})
export class FermetureAddAreaComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;
  protected readonly FermetureTabEnum = FermetureTabEnum;

  private readonly fermetureClientSvc = inject(FermetureClientService);

  joursSemaineItems = Object.keys(JourSemaine)
    .filter(k => isNaN(Number(k)))
    .map(k => ({label: k, value: JourSemaine[k as keyof typeof JourSemaine]}));

  activeTab = input.required<FermetureTabEnum>();

  jourLinesReady = output<FermetureJourSemaineDTO[]>();
  periodeLinesReady = output<FermeturePeriodeDTO[]>();
  closeRequested = output();

  private readonly hierarchyData = computed(() =>
    this.fermetureClientSvc.hierarchyResource.value() ?? {clients: [], plcsByClientId: {}, prestationsByPlcId: {}}
  );

  clientList = computed(() => this.hierarchyData().clients);
  plcList = signal<HierarchyPropertyDTO[]>([]);
  prestationList = signal<HierarchyPropertyDTO[]>([]);

  // ── Sélections cascadantes ───────────────────────────────────────────────
  selectedClientIds = signal<number[]>([]);
  selectedPlcIds = signal<number[]>([]);
  selectedPrestationIds = signal<number[]>([]);

  private aggregateItems(ids: number[], map: { [id: number]: HierarchyPropertyDTO[] }): HierarchyPropertyDTO[] {
    const result = new Map<number, HierarchyPropertyDTO>();
    ids.forEach(id => (map[id] ?? []).forEach(item => result.set(item.id, item)));
    return Array.from(result.values());
  }

  onClientsChanged = (ids: number[]): void => {
    this.selectedClientIds.set(ids);
    this.plcList.set(this.aggregateItems(ids, this.hierarchyData().plcsByClientId));
    this.selectedPlcIds.set([]);
    this.prestationList.set([]);
    this.selectedPrestationIds.set([]);
  };

  onPlcsChanged = (ids: number[]): void => {
    this.selectedPlcIds.set(ids);
    this.prestationList.set(this.aggregateItems(ids, this.hierarchyData().prestationsByPlcId));
    this.selectedPrestationIds.set([]);
  };

  onPrestationsChanged = (ids: number[]): void => this.selectedPrestationIds.set(ids);

  selectedJours = signal<JourSemaine[]>([]);
  onJoursChanged = (jours: JourSemaine[]): void => this.selectedJours.set(jours);

  selectedDateRange = signal<Date[]>([]);
  dateDebut = computed(() => this.selectedDateRange()[0] ?? null);
  dateFin = computed(() => this.selectedDateRange()[1] ?? null);
  selectedMotifId = signal<number | null>(null);

  onDateRangeChanged = (dates: Date[] | undefined): void => {
    this.selectedDateRange.set(dates?.length === 2 ? dates : []);
  };
  onMotifChanged = (e: any): void => this.selectedMotifId.set(e.value ?? null);

  readonly motifsFermetureResource = this.fermetureClientSvc.motifsFermetureResource;

  // ── Validation ───────────────────────────────────────────────────────────
  private hasBaseSelection = computed(() =>
    this.selectedClientIds().length > 0
    && this.selectedPlcIds().length > 0
    && this.selectedPrestationIds().length > 0
  );

  canAddJour = computed(() => this.hasBaseSelection() && this.selectedJours().length > 0);
  canAddPeriode = computed(() => this.hasBaseSelection() && !!this.dateDebut() && !!this.dateFin());

  // ── Reset ────────────────────────────────────────────────────────────────
  reset(): void {
    this.selectedClientIds.set([]);
    this.selectedPlcIds.set([]);
    this.selectedPrestationIds.set([]);
    this.plcList.set([]);
    this.prestationList.set([]);
    this.selectedJours.set([]);
    this.selectedDateRange.set([]);
    this.selectedMotifId.set(null);
  }

  // ── Génération des lignes ────────────────────────────────────────────────
  private getSelectionCombinations(): Array<{ client: HierarchyPropertyDTO, plc: HierarchyPropertyDTO, prestation: HierarchyPropertyDTO }> {
    const h = this.hierarchyData();
    const selectedClientIds = this.selectedClientIds();
    const selectedPlcIds = this.selectedPlcIds();
    const selectedPrestationIds = this.selectedPrestationIds();

    const results: Array<{ client: HierarchyPropertyDTO, plc: HierarchyPropertyDTO, prestation: HierarchyPropertyDTO }> = [];
    for (const client of h.clients.filter(c => selectedClientIds.includes(c.id))) {
      const plcs = (h.plcsByClientId[client.id] ?? []).filter(p => selectedPlcIds.includes(p.id));
      for (const plc of plcs) {
        const prestations = (h.prestationsByPlcId[plc.id] ?? []).filter(p => selectedPrestationIds.includes(p.id));
        for (const prestation of prestations) {
          results.push({client, plc, prestation});
        }
      }
    }
    return results;
  }

  addJour = (): void => {
    const jours = this.selectedJours();
    const lines: FermetureJourSemaineDTO[] = this.getSelectionCombinations().map(({client, plc, prestation}) => ({
      cmcPlcId: null,
      clientId: client.id,
      clientLibelle: client.libelle,
      pointLivraisonId: plc.id,
      pointLivraisonLibelle: plc.libelle,
      prestationId: prestation.id,
      prestationLibelle: prestation.libelle,
      jours
    }));
    this.jourLinesReady.emit(lines);
  };

  addPeriode = (): void => {
    const motifId = this.selectedMotifId();
    const motifLibelle = (this.fermetureClientSvc.motifsFermetureResource.value() ?? [])
      .find(m => m.id === motifId)?.libelle ?? null;
    const lines: FermeturePeriodeDTO[] = this.getSelectionCombinations().map(({client, plc, prestation}) => ({
      id: null,
      cmcPlcId: null,
      clientId: client.id,
      clientLibelle: client.libelle,
      pointLivraisonId: plc.id,
      pointLivraisonLibelle: plc.libelle,
      prestationId: prestation.id,
      prestationLibelle: prestation.libelle,
      dateDebut: this.dateDebut()!,
      dateFin: this.dateFin()!,
      motifId: motifId,
      motifLibelle
    }));
    this.periodeLinesReady.emit(lines);
  };
}
